import {
  AutoAwesome as AgentIcon,
  CheckCircle as AcceptIcon,
  Close as CloseIcon,
  DoNotDisturb as DismissIcon,
  GridView as KanbanIcon,
  NotificationsActive as SignalIcon,
  Search as SearchIcon,
  ViewList as ListIcon,
  ArrowForward as MoveIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import type { AccountStatus, AccountStreamMatch } from "../../types/accountIntelligence";
import { MOCK_ACCOUNT_STREAM_MATCHES, MOCK_STREAMS, getICPLabel } from "./mockData";
import AccountStateChip from "./shared/AccountStateChip";
import AccountWorkspace from "./AccountWorkspace";

// ─── Constants ────────────────────────────────────────────────────────────────

type TabValue = "monitoring" | "candidates" | "all";
type ViewMode = "list" | "kanban";

const MONITORING_STATUSES: AccountStatus[] = ["watching", "engaged"];
const CANDIDATE_STATUS: AccountStatus = "candidate";

const STATUS_LABELS: Record<AccountStatus, string> = {
  candidate: "Candidate",
  watching: "Watching",
  engaged: "Engaged",
  deprioritized: "Deprioritized",
};

const ALL_STATUSES: AccountStatus[] = ["candidate", "watching", "engaged", "deprioritized"];

const KANBAN_COLUMNS: Array<{ status: AccountStatus; color: string }> = [
  { status: "candidate", color: "#9e9e9e" },
  { status: "watching", color: "#1976d2" },
  { status: "engaged", color: "#2e7d32" },
  { status: "deprioritized", color: "#616161" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getLastSignalDate(match: AccountStreamMatch): Date | null {
  if (!match.signals.length) return null;
  return new Date(Math.max(...match.signals.map((s) => new Date(s.date).getTime())));
}

function formatLastActivity(match: AccountStreamMatch): string {
  const d = getLastSignalDate(match);
  if (!d) return "—";
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff}d ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function getICPShortLabel(streamId: string): string {
  return getICPLabel(streamId).split(" ").slice(0, 3).join(" ");
}

// ─── Account Detail Drawer ─────────────────────────────────────────────────────

const AccountDetailDrawer: React.FC<{
  match: AccountStreamMatch | null;
  onClose: () => void;
}> = ({ match, onClose }) => (
  <Drawer
    anchor="right"
    open={!!match}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: { xs: "100vw", sm: 680 },
        backgroundColor: "background.paper",
        backgroundImage: "none",
      },
    }}
  >
    {match && (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <AccountWorkspace
          accountId={match.account.id}
          streamId={match.streamId}
          onBack={onClose}
        />
      </Box>
    )}
  </Drawer>
);

// ─── Kanban card ──────────────────────────────────────────────────────────────

const KanbanCard: React.FC<{
  match: AccountStreamMatch;
  effectiveStatus: AccountStatus;
  onOpen: () => void;
  onMove: (matchId: string, toStatus: AccountStatus) => void;
}> = ({ match, effectiveStatus, onOpen, onMove }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const signalCount = match.signals.length;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 1.5,
          mb: 1,
          cursor: "pointer",
          "&:hover": { borderColor: "primary.main" },
          transition: "border-color 0.15s",
        }}
        onClick={onOpen}
      >
        <Typography variant="body2" fontWeight={700} mb={0.4} sx={{ lineHeight: 1.3 }}>
          {match.account.name}
        </Typography>
        {match.topAngle && (
          <Typography variant="caption" color="text.secondary" display="block" mb={0.75} sx={{ lineHeight: 1.4 }}>
            {match.topAngle.title}
          </Typography>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }} onClick={(e) => e.stopPropagation()}>
          <Chip
            label={getICPShortLabel(match.streamId)}
            size="small"
            sx={{ fontSize: "0.62rem", height: 18, flex: 1, minWidth: 0, color: "text.disabled" }}
          />
          {signalCount > 0 && (
            <Tooltip title={`${signalCount} signal${signalCount > 1 ? "s" : ""}`}>
              <Badge
                badgeContent={signalCount}
                color="warning"
                sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", minWidth: 14, height: 14 } }}
              >
                <SignalIcon sx={{ fontSize: 14, color: "text.disabled" }} />
              </Badge>
            </Tooltip>
          )}
          <Tooltip title="Move to…">
            <IconButton
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ color: "text.disabled", p: 0.25 }}
            >
              <MoveIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {ALL_STATUSES.filter((s) => s !== effectiveStatus).map((s) => (
          <MenuItem
            key={s}
            dense
            onClick={() => { onMove(match.id, s); setMenuAnchor(null); }}
          >
            <Typography variant="body2">{STATUS_LABELS[s]}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// ─── Kanban view ──────────────────────────────────────────────────────────────

const KanbanView: React.FC<{
  matches: AccountStreamMatch[];
  statusOverrides: Record<string, AccountStatus>;
  onOpen: (match: AccountStreamMatch) => void;
  onMove: (matchId: string, toStatus: AccountStatus) => void;
}> = ({ matches, statusOverrides, onOpen, onMove }) => (
  <Box sx={{ display: "flex", gap: 1.5, height: "100%", overflowX: "auto", p: 2, alignItems: "flex-start", justifyContent: "center" }}>
    {KANBAN_COLUMNS.map((col) => {
      const colMatches = matches.filter((m) => (statusOverrides[m.id] ?? m.status) === col.status);
      return (
        <Box key={col.status} sx={{ minWidth: 260, maxWidth: 280, display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              mb: 1.25,
              borderRadius: 1,
              backgroundColor: `${col.color}10`,
              border: `1px solid ${col.color}30`,
            }}
          >
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: col.color, flexShrink: 0 }} />
            <Typography variant="caption" fontWeight={700} sx={{ color: col.color, flex: 1 }}>
              {STATUS_LABELS[col.status]}
            </Typography>
            <Typography variant="caption" color="text.disabled">{colMatches.length}</Typography>
          </Box>
          <Box sx={{ overflowY: "auto", flex: 1 }}>
            {colMatches.length === 0 ? (
              <Typography variant="caption" color="text.disabled" sx={{ px: 1 }}>No accounts</Typography>
            ) : (
              colMatches.map((m) => (
                <KanbanCard
                  key={m.id}
                  match={m}
                  effectiveStatus={statusOverrides[m.id] ?? m.status}
                  onOpen={() => onOpen(m)}
                  onMove={onMove}
                />
              ))
            )}
          </Box>
        </Box>
      );
    })}
  </Box>
);

// ─── Table header ─────────────────────────────────────────────────────────────

const FIT_COLOR: Record<string, string> = {
  high: "#2e7d32",
  medium: "#f9a825",
  low: "#9e9e9e",
};

const ANGLE_STATUS_COLOR: Record<string, string> = {
  actionable: "#2e7d32",
  developing: "#e65100",
  watch: "#1976d2",
  dormant: "#616161",
  dismissed: "#424242",
};

const TableHeader: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: 2.5,
      py: 1,
      borderBottom: 1,
      borderColor: "divider",
      backgroundColor: "rgba(255,255,255,0.03)",
    }}
  >
    {[
      ["Account", "1 1 0"],
      ["ICP", "0 0 140px"],
      ["Status", "0 0 120px"],
      ["Fit", "0 0 72px"],
      ["Signals", "0 0 170px"],
      ["Angles", "0 0 130px"],
      ["Last Signal", "0 0 80px"],
    ].map(([label, flex]) => (
      <Typography key={label} variant="caption" fontWeight={700} color="text.disabled" sx={{ flex }}>
        {label}
      </Typography>
    ))}
  </Box>
);

// ─── List row ─────────────────────────────────────────────────────────────────

const AccountRow: React.FC<{
  match: AccountStreamMatch;
  effectiveStatus: AccountStatus;
  statusOverrides: Record<string, AccountStatus>;
  onOpenName: () => void;
  onStatusChange: (matchId: string, status: AccountStatus) => void;
}> = ({ match, effectiveStatus, onOpenName, onStatusChange }) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const signalCount = match.signals.length;
  const angleCount = match.angles.length;
  const lastActivity = formatLastActivity(match);
  const hasRecentSignal = signalCount > 0 && (lastActivity === "Today" || lastActivity === "Yesterday");
  const latestSignal = match.signals[0] ?? null;
  const topAngle = match.topAngle ?? null;
  const fitColor = FIT_COLOR[match.fitScore] ?? "#9e9e9e";
  const angleStatusColor = topAngle ? (ANGLE_STATUS_COLOR[topAngle.status] ?? "#9e9e9e") : null;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          px: 2.5,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          "&:hover": { backgroundColor: "action.hover" },
          transition: "background-color 0.12s",
        }}
      >
        {/* Account name + angle title + geography */}
        <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            noWrap
            sx={{ cursor: "pointer", "&:hover": { color: "primary.light", textDecoration: "underline" } }}
            onClick={onOpenName}
          >
            {match.account.name}
          </Typography>
          {topAngle && (
            <Typography
              variant="caption"
              noWrap
              display="block"
              sx={{ color: angleStatusColor ?? "text.secondary", mt: 0.2, fontStyle: "italic" }}
            >
              {topAngle.title}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" noWrap display="block" mt={topAngle ? 0.15 : 0.2}>
            {match.account.geography} · {match.account.accountType}
          </Typography>
        </Box>

        {/* ICP */}
        <Box sx={{ flex: "0 0 140px", pt: 0.25 }}>
          <Chip
            label={getICPShortLabel(match.streamId)}
            size="small"
            sx={{ fontSize: "0.68rem", color: "text.secondary", maxWidth: 136 }}
          />
        </Box>

        {/* Status — clickable to change */}
        <Box sx={{ flex: "0 0 120px", pt: 0.25 }}>
          <Box
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{ display: "inline-flex", cursor: "pointer", "&:hover": { opacity: 0.8 } }}
          >
            <AccountStateChip state={effectiveStatus} />
          </Box>
        </Box>

        {/* Fit level */}
        <Box sx={{ flex: "0 0 72px", pt: 0.35 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: fitColor, flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: fitColor, fontWeight: 600, textTransform: "capitalize" }}>
              {match.fitScore}
            </Typography>
          </Box>
        </Box>

        {/* Signals + latest signal title */}
        <Box sx={{ flex: "0 0 170px" }}>
          {signalCount > 0 ? (
            <>
              <Chip
                icon={<SignalIcon sx={{ fontSize: 11 }} />}
                label={`${signalCount} signal${signalCount > 1 ? "s" : ""}`}
                size="small"
                sx={{
                  fontSize: "0.68rem",
                  color: hasRecentSignal ? "#e65100" : "text.secondary",
                  backgroundColor: hasRecentSignal ? "rgba(230,81,0,0.1)" : "rgba(255,255,255,0.05)",
                  border: hasRecentSignal ? "1px solid rgba(230,81,0,0.3)" : "none",
                }}
              />
              {latestSignal && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  noWrap
                  display="block"
                  mt={0.35}
                  sx={{ maxWidth: 164 }}
                >
                  {latestSignal.title}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </Box>

        {/* Angles + top angle status */}
        <Box sx={{ flex: "0 0 130px" }}>
          {angleCount > 0 ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <AgentIcon sx={{ fontSize: 12, color: "primary.main" }} />
                <Typography variant="caption" color="primary.main" fontWeight={600}>
                  {angleCount} angle{angleCount > 1 ? "s" : ""}
                </Typography>
              </Box>
              {topAngle && angleStatusColor && (
                <Typography
                  variant="caption"
                  display="block"
                  mt={0.35}
                  sx={{ color: angleStatusColor, textTransform: "capitalize" }}
                >
                  {topAngle.status}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" color="text.disabled">—</Typography>
          )}
        </Box>

        {/* Last signal date */}
        <Box sx={{ flex: "0 0 80px", pt: 0.35 }}>
          <Typography variant="caption" color={hasRecentSignal ? "warning.main" : "text.disabled"}>
            {lastActivity}
          </Typography>
        </Box>
      </Box>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {ALL_STATUSES.filter((s) => s !== effectiveStatus).map((s) => (
          <MenuItem
            key={s}
            dense
            onClick={() => { onStatusChange(match.id, s); setMenuAnchor(null); }}
          >
            <Typography variant="body2">{STATUS_LABELS[s]}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// ─── Candidate row — inline accept/dismiss ─────────────────────────────────────

const CandidateRow: React.FC<{
  match: AccountStreamMatch;
  onOpenName: () => void;
  onAccept: (matchId: string) => void;
  onDismiss: (matchId: string) => void;
}> = ({ match, onOpenName, onAccept, onDismiss }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      px: 2.5,
      py: 1.5,
      borderBottom: 1,
      borderColor: "divider",
      "&:hover": { backgroundColor: "action.hover" },
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="body2"
        fontWeight={700}
        noWrap
        sx={{ cursor: "pointer", "&:hover": { color: "primary.light", textDecoration: "underline" } }}
        onClick={onOpenName}
      >
        {match.account.name}
      </Typography>
      <Typography variant="caption" color="text.disabled" noWrap display="block">
        {match.account.geography} · {match.account.accountType}
      </Typography>
      {match.fitRationale && (
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25} sx={{ maxWidth: 480 }}>
          {match.fitRationale.slice(0, 100)}…
        </Typography>
      )}
    </Box>
    <Chip label={getICPShortLabel(match.streamId)} size="small" sx={{ fontSize: "0.68rem", color: "text.secondary", flexShrink: 0 }} />
    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
      <Button
        size="small"
        variant="contained"
        startIcon={<AcceptIcon sx={{ fontSize: 14 }} />}
        onClick={() => onAccept(match.id)}
        sx={{ textTransform: "none", fontSize: "0.72rem" }}
      >
        Watch
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="error"
        startIcon={<DismissIcon sx={{ fontSize: 14 }} />}
        onClick={() => onDismiss(match.id)}
        sx={{ textTransform: "none", fontSize: "0.72rem" }}
      >
        Dismiss
      </Button>
    </Box>
  </Box>
);

// ─── Main AccountPortfolio ─────────────────────────────────────────────────────

const AccountPortfolio: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabValue>("monitoring");
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedStreamIds, setSelectedStreamIds] = useState<Set<string>>(new Set());
  const [searchText, setSearchText] = useState("");
  const [selectedMatch, setSelectedMatch] = useState<AccountStreamMatch | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, AccountStatus>>({});

  const allMatches = MOCK_ACCOUNT_STREAM_MATCHES;

  const getStatus = (m: AccountStreamMatch): AccountStatus => statusOverrides[m.id] ?? m.status;

  const monitoringCount = allMatches.filter((m) => MONITORING_STATUSES.includes(getStatus(m))).length;
  const candidateCount = allMatches.filter((m) => getStatus(m) === CANDIDATE_STATUS).length;

  const filtered = useMemo(() => {
    let result = allMatches;

    if (activeTab === "monitoring") {
      result = result.filter((m) => MONITORING_STATUSES.includes(getStatus(m)));
    } else if (activeTab === "candidates") {
      result = result.filter((m) => getStatus(m) === CANDIDATE_STATUS);
    }

    if (selectedStreamIds.size > 0) {
      result = result.filter((m) => selectedStreamIds.has(m.streamId));
    }

    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter(
        (m) =>
          m.account.name.toLowerCase().includes(q) ||
          m.account.industry.toLowerCase().includes(q) ||
          m.account.geography.toLowerCase().includes(q),
      );
    }

    // Sort by most recent signal date; no signals → bottom
    return [...result].sort((a, b) => {
      const da = getLastSignalDate(a)?.getTime() ?? 0;
      const db = getLastSignalDate(b)?.getTime() ?? 0;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });
  }, [allMatches, activeTab, selectedStreamIds, searchText, statusOverrides]);

  const handleStatusChange = (matchId: string, status: AccountStatus): void =>
    setStatusOverrides((prev) => ({ ...prev, [matchId]: status }));

  const toggleStream = (streamId: string): void =>
    setSelectedStreamIds((prev) => {
      const next = new Set(Array.from(prev));
      if (next.has(streamId)) next.delete(streamId);
      else next.add(streamId);
      return next;
    });

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 0, flexShrink: 0 }}>
        <Typography variant="h5" fontWeight={700}>Account Portfolio</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.25} mb={2}>
          {monitoringCount} monitoring · {candidateCount} candidates
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ px: 3, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v: TabValue) => setActiveTab(v)}
          sx={{ minHeight: 40 }}
          TabIndicatorProps={{ style: { height: 2 } }}
        >
          {(
            [
              ["monitoring", `Monitoring (${monitoringCount})`],
              ["candidates", `Candidates (${candidateCount})`],
              ["all", `All (${allMatches.length})`],
            ] as [TabValue, string][]
          ).map(([val, label]) => (
            <Tab key={val} value={val} label={label} sx={{ textTransform: "none", minHeight: 40, py: 0 }} />
          ))}
        </Tabs>
      </Box>

      {/* Toolbar */}
      <Box
        sx={{
          px: 3,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.disabled" fontWeight={600} mr={0.25}>ICP:</Typography>
          <Chip
            label="All"
            size="small"
            onClick={() => setSelectedStreamIds(new Set())}
            sx={{
              fontSize: "0.72rem",
              cursor: "pointer",
              backgroundColor: selectedStreamIds.size === 0 ? "primary.main" : undefined,
              color: selectedStreamIds.size === 0 ? "white" : "text.secondary",
            }}
          />
          {MOCK_STREAMS.map((s) => {
            const selected = selectedStreamIds.has(s.id);
            return (
              <Chip
                key={s.id}
                label={s.icp.name.split(" ").slice(0, 3).join(" ")}
                size="small"
                onClick={() => toggleStream(s.id)}
                sx={{
                  fontSize: "0.72rem",
                  cursor: "pointer",
                  backgroundColor: selected ? "primary.main" : undefined,
                  color: selected ? "white" : "text.secondary",
                  maxWidth: 240,
                }}
              />
            );
          })}
        </Box>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search accounts…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 200, "& .MuiOutlinedInput-root": { fontSize: "0.82rem" } }}
        />
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
          sx={{ "& .MuiToggleButton-root": { px: 1.25, py: 0.5 } }}
        >
          <Tooltip title="List view"><ToggleButton value="list"><ListIcon fontSize="small" /></ToggleButton></Tooltip>
          <Tooltip title="Kanban view"><ToggleButton value="kanban"><KanbanIcon fontSize="small" /></ToggleButton></Tooltip>
        </ToggleButtonGroup>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {viewMode === "list" ? (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {activeTab !== "candidates" && <TableHeader />}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {filtered.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography color="text.secondary">No accounts match the current filters.</Typography>
                </Box>
              ) : activeTab === "candidates" ? (
                filtered.map((m) => (
                  <CandidateRow
                    key={m.id}
                    match={m}
                    onOpenName={() => setSelectedMatch(m)}
                    onAccept={(id) => handleStatusChange(id, "watching")}
                    onDismiss={(id) => handleStatusChange(id, "deprioritized")}
                  />
                ))
              ) : (
                filtered.map((m) => (
                  <AccountRow
                    key={m.id}
                    match={m}
                    effectiveStatus={getStatus(m)}
                    statusOverrides={statusOverrides}
                    onOpenName={() => setSelectedMatch(m)}
                    onStatusChange={handleStatusChange}
                  />
                ))
              )}
            </Box>
          </Box>
        ) : (
          <KanbanView
            matches={activeTab === "all"
              ? filtered
              : allMatches.filter((m) => KANBAN_COLUMNS.some((c) => c.status === getStatus(m)))}
            statusOverrides={statusOverrides}
            onOpen={setSelectedMatch}
            onMove={handleStatusChange}
          />
        )}
      </Box>

      <AccountDetailDrawer match={selectedMatch} onClose={() => setSelectedMatch(null)} />
    </Box>
  );
};

export default AccountPortfolio;
